/**
 * AdminLayout — tabbed shell for /admin/*. Renders the tab nav + whatever
 * subroute matched inside it. Non-admins get the guard view.
 *
 * Tabs (URLs map 1:1 to files under apps/web/src/routes/admin/):
 *   /admin/overview   → AdminOverview.tsx
 *   /admin/users      → AdminUsers.tsx
 *   /admin/meetings   → AdminMeetings.tsx
 *   /admin/team       → AdminTeam.tsx
 *   /admin/glossary   → AdminGlossary.tsx
 *
 * Bare /admin redirects to /admin/overview.
 */
import { NavLink, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.tsx';

const TABS: { path: string; label: string }[] = [
  { path: '/admin/overview', label: 'Overview' },
  { path: '/admin/users', label: 'Users' },
  { path: '/admin/meetings', label: 'Meetings' },
  { path: '/admin/projects', label: 'Projects' },
  { path: '/admin/team', label: 'OJB Team' },
  { path: '/admin/glossary', label: 'Glossary' },
  { path: '/admin/archive', label: 'Archive' },
];

export function AdminLayout() {
  const { user } = useAuth();
  const location = useLocation();

  if (!user?.isAdmin) {
    return (
      <div className="mx-auto max-w-4xl px-8 py-12">
        <h1 className="text-2xl font-semibold text-charcoal">Admin</h1>
        <p className="mt-2 text-sm text-smoke">
          You need admin access to view this page.
        </p>
      </div>
    );
  }

  // Bare /admin → Overview.
  if (location.pathname === '/admin' || location.pathname === '/admin/') {
    return <Navigate to="/admin/overview" replace />;
  }

  return (
    // Mockup .admin-page: max-w 1280 (= max-w-7xl) + 24/40/80 padding
    // — wider than the dashboard's max-w-6xl because admin tables need
    // more horizontal room for their many columns.
    <div className="mx-auto max-w-7xl px-10 pb-20 pt-6">
      <header className="pb-2 pt-4">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-pine">
          OJB · Admin
        </div>
        {/* Mockup .admin-head h1: 32px Aeonik 400 + tracking-tight.
            Lighter weight than the dashboard's 56px personalized H1
            since admin is a utility surface, not a hero. */}
        <h1 className="mt-1.5 text-[32px] font-normal leading-[1.05] tracking-tight text-charcoal">
          Admin
        </h1>
      </header>

      {/* Mockup .admin-tabs: 13px medium with smoke/30 baseline
          (lighter than charcoal/15), active tab gets pine 2px
          underline + pine text. Hover hints pine. */}
      <nav className="mb-7 mt-6 flex gap-1 border-b border-smoke/30">
        {TABS.map((t) => (
          <NavLink
            key={t.path}
            to={t.path}
            className={({ isActive }) =>
              [
                'px-4 py-2.5 text-[13px] font-medium transition-colors',
                isActive
                  ? '-mb-px border-b-2 border-pine text-pine'
                  : '-mb-px border-b-2 border-transparent text-charcoal/70 hover:text-pine',
              ].join(' ')
            }
          >
            {t.label}
          </NavLink>
        ))}
      </nav>

      <Outlet />
    </div>
  );
}
